import React, { useId, useMemo } from 'react';
import { useTheme } from '@mui/material/styles';
import {
  INKY_CLASS,
  INKY_CSS,
  buildInkyTree,
  inkyRootClass,
  normalizeMood,
  sanitizeId,
} from './inkyArt';

/**
 * Inky, the Quill AI assistant: a small octopus drawn as inline SVG.
 *
 * The artwork lives in `./inkyArt.js` (plain data, shared with the standalone
 * preview). This file turns it into React elements, picks the palette from the
 * active MUI theme mode, and injects the scoped keyframes once per document.
 * Motion is off under `prefers-reduced-motion: reduce`.
 */

const STYLE_ELEMENT_ID = `${INKY_CLASS}-styles`;

/**
 * Inserts the mascot stylesheet into <head> once. Safe to call repeatedly and
 * a no-op outside the browser.
 */
function ensureInkyStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.setAttribute('data-owner', 'inky-mascot');
  style.textContent = INKY_CSS;
  document.head.appendChild(style);
}

// useInsertionEffect runs before layout effects, so the keyframes exist before
// the first paint; fall back for environments without it.
const useStyleInsertionEffect = React.useInsertionEffect || React.useLayoutEffect;

const STYLE_PROPERTY_PATTERN = /-([a-z])/g;

/**
 * Converts an SVG attribute name to its React prop name.
 * @param {string} name SVG attribute name.
 * @returns {string} React prop name.
 */
function toReactPropName(name) {
  if (name === 'class') return 'className';
  if (name.startsWith('aria-') || name.startsWith('data-')) return name;
  return name.replace(STYLE_PROPERTY_PATTERN, (_, ch) => ch.toUpperCase());
}

/**
 * Parses an inline style string (`a-b: c; d: e`) into a React style object.
 * @param {string} css Inline style.
 * @returns {Object} Style object.
 */
function parseStyle(css) {
  return String(css)
    .split(';')
    .map((decl) => decl.trim())
    .filter(Boolean)
    .reduce((acc, decl) => {
      const idx = decl.indexOf(':');
      if (idx > 0) {
        const prop = decl.slice(0, idx).trim().replace(STYLE_PROPERTY_PATTERN, (_, ch) => ch.toUpperCase());
        acc[prop] = decl.slice(idx + 1).trim();
      }
      return acc;
    }, {});
}

/**
 * Converts an art node tree into React elements.
 * @param {{ tag: string, attrs: Object, children: Array }} node Node.
 * @param {string} key React key.
 * @returns {React.ReactElement} Element.
 */
function nodeToReact(node, key) {
  const props = { key };
  Object.entries(node.attrs).forEach(([name, value]) => {
    if (value === undefined || value === null || value === false) return;
    if (name === 'style') {
      props.style = parseStyle(value);
    } else {
      props[toReactPropName(name)] = value;
    }
  });
  const children = node.children.map((child, index) => nodeToReact(child, `${key}.${index}`));
  return React.createElement(node.tag, props, ...children);
}

/**
 * Inky mascot.
 *
 * @param {Object} props
 * @param {number} [props.size=32] Rendered width and height in px.
 * @param {'idle'|'thinking'|'writing'|'happy'|'error'} [props.mood='idle'] Expression and motion.
 * @param {boolean} [props.animated=true] Enables the mood's animation (never runs under reduced motion).
 * @param {string} [props.title] Accessible name. Defaults to "Inky".
 * @param {boolean} [props.decorative=false] Hides the image from assistive tech (use when adjacent text already says "Inky").
 * @param {'light'|'dark'} [props.scheme] Forces a palette; defaults to the MUI theme mode.
 * @param {string} [props.className] Extra class names for the <svg>.
 * @param {Object} [props.style] Inline style for the <svg>.
 * @returns {JSX.Element} SVG element.
 */
export default function InkyMascot({
  size = 32,
  mood = 'idle',
  animated = true,
  title,
  decorative = false,
  scheme,
  className,
  style,
  ...rest
}) {
  const theme = useTheme();
  const reactId = useId();
  const idPrefix = `inky-${sanitizeId(reactId)}`;
  const resolvedScheme = scheme || (theme?.palette?.mode === 'dark' ? 'dark' : 'light');
  const resolvedMood = normalizeMood(mood);

  useStyleInsertionEffect(ensureInkyStyles, []);

  const content = useMemo(
    () => nodeToReact(buildInkyTree({ mood: resolvedMood, scheme: resolvedScheme, idPrefix }), 'inky'),
    [resolvedMood, resolvedScheme, idPrefix],
  );

  const label = title || 'Inky';
  const a11yProps = decorative
    ? { 'aria-hidden': true, focusable: 'false' }
    : { role: 'img', 'aria-label': label };

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 64 64"
      className={inkyRootClass(resolvedMood, animated, className)}
      style={style}
      data-mood={resolvedMood}
      {...a11yProps}
      {...rest}
    >
      {!decorative && <title>{label}</title>}
      {content}
    </svg>
  );
}

/**
 * Circular soft-accent badge with Inky inside, for chat message avatars and
 * compact headers.
 *
 * @param {Object} props
 * @param {number} [props.size=32] Badge diameter in px (designed for 24-40).
 * @param {'idle'|'thinking'|'writing'|'happy'|'error'} [props.mood='idle'] Mood.
 * @param {boolean} [props.animated=false] Avatars repeat down a conversation, so motion is opt-in.
 * @param {string} [props.title] Accessible name. Defaults to "Inky".
 * @param {boolean} [props.decorative=false] Hides the badge from assistive tech.
 * @param {string} [props.className] Extra class names.
 * @param {Object} [props.style] Inline style overrides.
 * @returns {JSX.Element} Badge.
 */
export function InkyAvatar({
  size = 32,
  mood = 'idle',
  animated = false,
  title,
  decorative = false,
  className,
  style,
  ...rest
}) {
  const theme = useTheme();
  const isDark = theme?.palette?.mode === 'dark';
  const tokens = theme?.quill?.tokens;
  const background = tokens?.accentSoft || (isDark ? '#2A2450' : '#ECE8FC');
  const ring = tokens?.accentBorder || (isDark ? '#3E3577' : '#DDD7F7');
  const label = title || 'Inky';
  const a11yProps = decorative ? { 'aria-hidden': true } : { role: 'img', 'aria-label': label };

  return (
    <span
      className={[`${INKY_CLASS}-avatar`, className].filter(Boolean).join(' ')}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background,
        boxShadow: `inset 0 0 0 1px ${ring}`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        overflow: 'hidden',
        ...style,
      }}
      {...a11yProps}
      {...rest}
    >
      <InkyMascot
        size={Math.round(size * 0.84)}
        mood={mood}
        animated={animated}
        decorative
        style={{ marginTop: Math.round(size * 0.06) }}
      />
    </span>
  );
}

/**
 * Busy indicator: Inky plus a label with an animated ellipsis. Announced
 * politely to screen readers (the ellipsis is hidden from them).
 *
 * @param {Object} props
 * @param {React.ReactNode} [props.label='Inky is thinking'] Status text, without trailing dots.
 * @param {'idle'|'thinking'|'writing'|'happy'|'error'} [props.mood='thinking'] Mood.
 * @param {number} [props.size=28] Mascot size in px.
 * @param {boolean} [props.animated=true] Enables motion.
 * @param {string} [props.className] Extra class names.
 * @param {Object} [props.style] Inline style for the wrapper.
 * @param {Object} [props.labelStyle] Inline style for the label text.
 * @returns {JSX.Element} Status element.
 */
export function InkyWorking({
  label = 'Inky is thinking',
  mood = 'thinking',
  size = 28,
  animated = true,
  className,
  style,
  labelStyle,
  ...rest
}) {
  const theme = useTheme();
  const color = theme?.palette?.text?.secondary;
  const typography = theme?.typography?.body2 || {};
  const showEllipsis = animated && normalizeMood(mood) !== 'error';

  return (
    <span
      role="status"
      aria-live="polite"
      className={[`${INKY_CLASS}-working`, className].filter(Boolean).join(' ')}
      style={{ color, fontSize: typography.fontSize, fontWeight: 500, lineHeight: 1.4, ...style }}
      {...rest}
    >
      <InkyMascot size={size} mood={mood} animated={animated} decorative />
      <span className={`${INKY_CLASS}-working__label`} style={labelStyle}>
        {label}
        {showEllipsis && (
          <span className={`${INKY_CLASS}-ellipsis`} aria-hidden="true">
            <span>.</span>
            <span>.</span>
            <span>.</span>
          </span>
        )}
      </span>
    </span>
  );
}

/**
 * Larger Inky on a soft halo, for empty states and onboarding. Decorative by
 * default because empty states carry their own heading.
 *
 * @param {Object} props
 * @param {number} [props.size=72] Halo diameter in px.
 * @param {'idle'|'thinking'|'writing'|'happy'|'error'} [props.mood='happy'] Mood.
 * @param {boolean} [props.animated=true] Enables motion.
 * @param {string} [props.title] When given, the mark is exposed as an image with this name.
 * @param {string} [props.className] Extra class names.
 * @param {Object} [props.style] Inline style overrides.
 * @returns {JSX.Element} Mark.
 */
export function InkyLogoMark({ size = 72, mood = 'happy', animated = true, title, className, style, ...rest }) {
  const theme = useTheme();
  const isDark = theme?.palette?.mode === 'dark';
  const tokens = theme?.quill?.tokens;
  const halo = tokens?.accentTint || (isDark ? '#221E3D' : '#F7F5FE');
  const ring = tokens?.accentSoft || (isDark ? '#2A2450' : '#ECE8FC');
  const a11yProps = title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true };

  return (
    <span
      className={[`${INKY_CLASS}-mark`, className].filter(Boolean).join(' ')}
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        background: `radial-gradient(circle at 50% 42%, ${halo} 0%, ${halo} 55%, ${ring} 100%)`,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        flex: 'none',
        ...style,
      }}
      {...a11yProps}
      {...rest}
    >
      <InkyMascot size={Math.round(size * 0.78)} mood={mood} animated={animated} decorative />
    </span>
  );
}
